import { useEffect } from "preact/hooks";
import {
    ApproveBudgetKind,
    ApproveEntityStatus,
    ApproveKind,
    Decimal,
} from "@kase/my-marathon-communication";
import {
    signal,
    useComputed,
    useSignal,
    useSignalEffect,
} from "@preact/signals";
import { ApproveBudget, ApproveExpense, ApproveInvoice, useApp } from "~/core";
import { dateFromApi, upsert } from "~/utils";
import { ApprovalWidgetView } from "../../../shared";
import { SingleWidgetProps } from "../interfaces";

const onlyOpen = (
    approval: ApproveExpense | ApproveInvoice | ApproveBudget,
) => {
    const m = approval.mine.peek();

    return (
        approval.status === ApproveEntityStatus.ApproveOpen &&
        (!m ||
            (approval.type === "budget" &&
                m.kind === ApproveBudgetKind.BudgetKindNone) ||
            (approval.type !== "budget" && m.kind === ApproveKind.None))
    );
};

const useWidget = ({}: SingleWidgetProps) => {
    const { api } = useApp();
    const apiFilter = useComputed(() => ({
        statuses: [ApproveEntityStatus.ApproveOpen],
        companies: api.company.approve.value.map((c) => c.code),
    }));
    const loading = useComputed(
        () => api.approve.loading(apiFilter.value).value,
    );
    const approvals = useSignal<ApprovalWidgetView[]>([]);

    useEffect(() => {
        const viewInvoice = (invoice: ApproveInvoice): ApprovalWidgetView => ({
            id: invoice.id,
            type: "invoice",
            dueDate: dateFromApi(invoice.due_date),
            supplier: api.supplier.get(invoice.supplier, invoice.company),
            employee: null,
            project: null,
            amount: Decimal.from(invoice.amount).toNumber(),
            company: invoice.company,
            raw: invoice,
            tag: signal(0),
        });

        const viewExpense = (expense: ApproveExpense): ApprovalWidgetView => ({
            id: expense.id,
            type: "expense",
            dueDate: expense.date ? dateFromApi(expense.date) : null,
            supplier: null,
            employee: api.employee.get(expense.employee),
            project: null,
            amount: Decimal.from(expense.amount).toNumber(),
            company: expense.company,
            raw: expense,
            tag: signal(0),
        });

        const viewBudget = (budget: ApproveBudget): ApprovalWidgetView => ({
            id: budget.id,
            type: "budget",
            dueDate: budget.date ? dateFromApi(budget.date) : null,
            supplier: null,
            employee: null,
            project: api.project.getWithCompany(budget.project, budget.company),
            amount: Decimal.from(budget.amount).toNumber(),
            company: budget.company,
            raw: budget,
            tag: signal(0),
        });

        const handleAddInvoice = (invoices: ApproveInvoice[]) => {
            const newItems = invoices.filter(onlyOpen);

            if (newItems.length > 0) {
                approvals.value = upsert(
                    approvals.peek().slice(),
                    newItems.map(viewInvoice),
                    "id",
                );
            }
        };

        const handleAddExpense = (expenses: ApproveExpense[]) => {
            const newItems = expenses.filter(onlyOpen);

            if (newItems.length > 0) {
                approvals.value = upsert(
                    approvals.peek().slice(),
                    newItems.map(viewExpense),
                    "id",
                );
            }
        };

        const handleAddBudget = (budgets: ApproveBudget[]) => {
            const newItems = budgets.filter(onlyOpen);

            if (newItems.length > 0) {
                approvals.value = upsert(
                    approvals.peek().slice(),
                    newItems.map(viewBudget),
                    "id",
                );
            }
        };

        const handleDelete = (
            items: (ApproveExpense | ApproveInvoice | ApproveBudget)[],
        ) => {
            approvals.value = approvals
                .peek()
                .filter((a) => !items.find((i) => i.id === a.id));
        };

        const handleRelatedUpdate = () => {
            for (const approval of approvals.peek()) {
                if (approval.type === "expense" && !approval.employee) {
                    const employee = api.employee.get(approval.raw.employee);

                    if (employee) {
                        approval.employee = employee;
                        approval.tag.value = approval.tag.peek() + 1;
                    }
                } else if (approval.type === "invoice" && !approval.supplier) {
                    const supplier = api.supplier.get(
                        approval.raw.supplier,
                        approval.company,
                    );

                    if (supplier) {
                        approval.supplier = supplier;
                        approval.tag.value = approval.tag.peek() + 1;
                    }
                } else if (approval.type === "budget" && !approval.project) {
                    const project = api.project.getWithCompany(
                        approval.raw.project,
                        approval.company,
                    );

                    if (project) {
                        approval.project = project;
                        approval.tag.value = approval.tag.peek() + 1;
                    }
                }
            }
        };

        const handleAddMine = () => {
            const newItems = approvals
                .peek()
                .filter((item) => onlyOpen(item.raw));

            if (newItems.length !== approvals.peek().length) {
                approvals.value = newItems;
            }
        };

        const handleRemoveMine = () => {
            const newInvoices = api.approve.invoices.peek().filter(onlyOpen);
            const newExpenses = api.approve.expenses.peek().filter(onlyOpen);
            const newBudgets = api.approve.budgets.peek().filter(onlyOpen);
            const newLength =
                newInvoices.length + newExpenses.length + newBudgets.length;

            if (newLength !== approvals.peek().length) {
                approvals.value = [
                    ...newInvoices.map(viewInvoice),
                    ...newExpenses.map(viewExpense),
                    ...newBudgets.map(viewBudget),
                ];
            }
        };

        api.approve.approval.on("add", handleAddMine);
        api.approve.approval.on("remove", handleRemoveMine);
        api.approve.onInvoice("add", handleAddInvoice);
        api.approve.onInvoice("remove", handleDelete);
        api.approve.onExpense("add", handleAddExpense);
        api.approve.onExpense("remove", handleDelete);
        api.approve.onBudget("add", handleAddBudget);
        api.approve.onBudget("remove", handleDelete);
        api.supplier.on("add", handleRelatedUpdate);
        api.employee.on("add", handleRelatedUpdate);

        const unsubscribeProject = api.project.loaded.subscribe((done) => {
            if (done) {
                handleRelatedUpdate();
            }
        });

        handleAddInvoice(api.approve.invoices.peek());
        handleAddExpense(api.approve.expenses.peek());
        handleAddBudget(api.approve.budgets.peek());

        return () => {
            api.approve.approval.off("add", handleAddMine);
            api.approve.approval.off("remove", handleRemoveMine);
            api.approve.offInvoice("add", handleAddInvoice);
            api.approve.offInvoice("remove", handleDelete);
            api.approve.offExpense("add", handleAddExpense);
            api.approve.offExpense("remove", handleDelete);
            api.supplier.off("add", handleRelatedUpdate);
            api.employee.off("add", handleRelatedUpdate);
            unsubscribeProject();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        api.supplier.listen();
    }, [api]);

    useSignalEffect(() => {
        api.approve.listen(apiFilter.value);
    });

    return { loading, approvals };
};

export default useWidget;
